// NEW_FILE_JUSTIFIED: New messages feature component

import { memo } from 'react';
import { AtSign, Phone, Pencil, User, ChevronDown, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { AiContactSettings } from '@/components/aiReply/AiContactSettings';
import { AiNotesSection } from '@/components/aiReply/AiNotesSection';
import type {
  IContactPanelProps,
  IContactHeaderProps,
  IContactInfoSectionProps,
  ITagsSectionProps,
} from '@/@types/messages';

// ============================================================================
// Sub-components
// ============================================================================

function ContactHeader({
  name,
  initials,
  avatarColor,
  contactType,
  contactTypeColor,
  profilePicUrl,
}: IContactHeaderProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      {profilePicUrl !== undefined ? (
        <img
          src={profilePicUrl}
          alt={name}
          className="size-[72px] shrink-0 rounded-full object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex size-[72px] shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: avatarColor }}
        >
          <span className="text-foreground text-2xl font-semibold">{initials}</span>
        </div>
      )}

      <div className="flex flex-col items-center gap-2">
        <span className="text-foreground text-lg text-center font-semibold">{name}</span>

        <span
          className="text-xs rounded-full px-2.5 py-1 font-medium"
          style={{
            color: contactTypeColor,
            backgroundColor: contactTypeColor + '20',
          }}
        >
          {contactType}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {(
          [
            { Icon: Phone, label: 'Call' },
            { Icon: Pencil, label: 'Edit contact' },
          ] as const
        ).map(({ Icon, label }) => (
          <button
            key={label}
            aria-label={label}
            className={cn(
              'flex size-9 items-center justify-center rounded-lg',
              'bg-muted text-muted-foreground'
            )}
          >
            <Icon size={16} />
          </button>
        ))}
      </div>
    </div>
  );
}

function ContactInfoSection({ username, phone, bio, assignedTo }: IContactInfoSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-foreground text-sm font-semibold">Contact</span>
        <ChevronDown size={16} className="text-muted-foreground" aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-2.5">
        {username !== undefined && (
          <div className="flex items-center gap-3">
            <AtSign size={16} className="text-muted-foreground shrink-0" />
            <span className="text-foreground text-[13px]">@{username}</span>
          </div>
        )}

        {phone !== undefined && (
          <div className="flex items-center gap-3">
            <Phone size={16} className="text-muted-foreground shrink-0" />
            <span className="text-foreground text-[13px]">{phone}</span>
          </div>
        )}

        {bio !== undefined && bio !== '' && (
          <div className="flex items-start gap-3">
            <FileText size={16} className="text-muted-foreground shrink-0 mt-0.5" />
            <span className="text-foreground text-[13px]">{bio}</span>
          </div>
        )}

        {assignedTo !== '' && (
          <div className="flex items-center gap-3">
            <User size={16} className="text-muted-foreground shrink-0" />
            <span className="text-muted-foreground text-[13px]">Assigned to: {assignedTo}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function TagsSection({ tags }: ITagsSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-foreground text-sm font-semibold">Tags</span>
        <ChevronDown size={16} className="text-muted-foreground" aria-hidden="true" />
      </div>

      <div className="flex flex-wrap gap-2">
        {tags.map(tag => (
          <span
            key={tag.id}
            className="text-xs rounded-full px-2.5 py-1 font-medium"
            style={{
              color: tag.color,
              backgroundColor: tag.bgColor,
            }}
          >
            {tag.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export const ContactPanel = memo(function ContactPanel({
  contact,
  tags,
  contactIdentifier,
  platform,
}: IContactPanelProps) {
  const isGroup = contact.chatType === 'group';
  const showAiSettings = contactIdentifier !== undefined && platform !== undefined;

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-l border-msg-panel-border bg-msg-panel-bg">
      <ScrollArea className="h-full">
        <div className="flex flex-col gap-5 p-5">
          <ContactHeader
            name={contact.name}
            initials={contact.initials}
            avatarColor={contact.avatarColor}
            contactType={contact.contactType}
            contactTypeColor={contact.contactTypeColor}
            profilePicUrl={contact.profilePicUrl}
          />

          {!isGroup && (
            <>
              <Separator className="bg-msg-panel-border" />

              <ContactInfoSection
                username={contact.username}
                phone={contact.phone}
                bio={contact.bio}
                assignedTo={contact.assignedTo}
              />
            </>
          )}

          <Separator className="bg-msg-panel-border" />

          <TagsSection tags={tags} />

          {showAiSettings && (
            <>
              <Separator className="bg-msg-panel-border" />

              <AiContactSettings
                contactIdentifier={contactIdentifier}
                platform={platform}
                contactName={contact.name}
              />
            </>
          )}

          {showAiSettings && (
            <>
              <Separator className="bg-msg-panel-border" />

              <AiNotesSection
                contactIdentifier={contactIdentifier}
                platform={platform}
              />
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
});
